import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ScrollText } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { match, type MatchAccount, type MatchEntryDetail, type MatchLedgerEntry } from '../api';
import { MatchShell } from './MatchShell';
import { ActionQueueCard, MetricStrip, StickySummaryBar, TimelineRow } from '../ui/mobileOps';
import { SectionCard } from '../ui/components';
import { Button, EmptyState, ErrorBanner, Pill, Spinner } from '../ui/primitives';

export function MatchEntryDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const valid = Number.isFinite(id) && id > 0;

  const entryQ = useQuery<MatchEntryDetail>({
    queryKey: ['match-entry', id],
    queryFn: () => match.getEntry(id),
    enabled: valid,
  });

  const accountsQ = useQuery<MatchAccount[]>({
    queryKey: ['match-accounts', 'all'],
    queryFn: () => match.listAccounts({ status: 'all' }),
  });

  const accountsById = useMemo(
    () => new Map((accountsQ.data ?? []).map((account) => [account.id, account])),
    [accountsQ.data],
  );

  if (!valid) {
    return (
      <MatchShell title="Entry detail" subtitle="Match entry">
        <SectionCard section="match">
          <ErrorBanner>Not a valid Match entry id.</ErrorBanner>
        </SectionCard>
      </MatchShell>
    );
  }

  if (entryQ.isLoading || !entryQ.data) {
    return (
      <MatchShell title="Entry detail" subtitle="Match entry">
        <SectionCard section="match">
          {entryQ.isError ? (
            <ErrorBanner>
              {entryQ.error instanceof Error ? entryQ.error.message : 'Failed to load the entry.'}
            </ErrorBanner>
          ) : (
            <div className="flex items-center gap-2 text-small text-ink-muted">
              <Spinner /> loading...
            </div>
          )}
        </SectionCard>
      </MatchShell>
    );
  }

  const { entry, ledgerEntries } = entryQ.data;
  const sourceAccount = accountsById.get(entry.sourceMatchAccountId) ?? null;
  const splitCount = ledgerEntries.filter((row) => row.partKind === 'split').length;
  const remainderCount = ledgerEntries.filter((row) => row.partKind === 'remainder').length;
  const stateTone = entry.voidedAt ? 'neg' : 'pos';

  return (
    <MatchShell
      title={`Entry #${entry.id}`}
      subtitle="Posted source movement and routed ledger rows"
      back={{ to: '/match', label: 'Match' }}
    >
      <div className="space-y-4">
        <div className="md:hidden">
          <StickySummaryBar
            section="match"
            stackedBelowTabs
            title={sourceAccount?.name ?? `Entry #${entry.id}`}
            subtitle={entry.note?.trim() || 'Signed amount posted into the match ledger.'}
            metrics={[
              { label: 'Amount', value: formatKinr(entry.amount), tone: amountTone(entry.amount) },
              { label: 'Rows', value: String(ledgerEntries.length) },
              { label: 'State', value: entry.voidedAt ? 'Voided' : 'Live', tone: stateTone },
            ]}
          />
        </div>

        <div className="hidden md:block">
          <MetricStrip
            section="match"
            items={[
              { label: 'Source amount', value: formatKinr(entry.amount), tone: amountTone(entry.amount) },
              { label: 'Split rows', value: String(splitCount), note: 'configured targets' },
              { label: 'Remainder rows', value: String(remainderCount), note: 'stayed on source' },
            ]}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={sourceAccount ? `/match/accounts/${entry.sourceMatchAccountId}/ledger` : '/match'}
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-border/70 bg-surface px-3 py-1.5 text-small font-medium text-ink-muted shadow-sm backdrop-blur transition-colors hover:bg-surface-raised hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
            <span>{sourceAccount ? 'Back to source ledger' : 'Back to Match'}</span>
          </Link>
          {entry.voidedAt && <Pill tone="red">Voided</Pill>}
          {entry.hadOverride && <Pill section="match">Override splits</Pill>}
        </div>

        <div className="grid gap-4 lg:grid-cols-[0.95fr_1.05fr]">
          <ActionQueueCard
            section="match"
            eyebrow="Entry summary"
            title={sourceAccount?.name ?? `Source #${entry.sourceMatchAccountId}`}
            description={entry.note?.trim() || 'No note was added when this entry was posted.'}
            meta={
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span>Effective date</span>
                  <span className="font-medium text-ink">{entry.effectiveDate}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Created</span>
                  <span className="font-medium text-ink">{new Date(entry.createdAt).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Source account</span>
                  <span className="font-medium text-ink">{sourceAccount?.name ?? `#${entry.sourceMatchAccountId}`}</span>
                </div>
                {entry.voidedAt && (
                  <div className="flex items-center justify-between gap-3">
                    <span>Voided at</span>
                    <span className="font-medium text-ink">{new Date(entry.voidedAt).toLocaleString()}</span>
                  </div>
                )}
              </div>
            }
            actions={
              sourceAccount ? (
                <Link to={`/match/accounts/${entry.sourceMatchAccountId}`}>
                  <Button size="sm" variant="secondary">
                    Open source account
                  </Button>
                </Link>
              ) : undefined
            }
          />

          <SectionCard section="match" title="Distribution breakdown">
            {ledgerEntries.length === 0 ? (
              <EmptyState
                section="match"
                icon={ScrollText}
                title="No ledger rows"
                description="This entry does not have any routed ledger output yet."
              />
            ) : (
              <div className="space-y-3">
                {ledgerEntries.map((row) => (
                  <div
                    key={row.id}
                    className="rounded-[22px] border border-border/70 bg-surface p-4 shadow-sm backdrop-blur"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-small font-semibold tracking-[-0.01em] text-ink">
                        {targetName(accountsById, row.targetMatchAccountId)}
                      </div>
                      <Pill tone={row.partKind === 'split' ? 'amber' : 'neutral'}>{row.partKind}</Pill>
                      {row.reversesEntryId !== null && <Pill tone="red">reversal</Pill>}
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-3 text-[13px] leading-5 text-ink-muted">
                      <span>{row.label?.trim() || (row.partKind === 'remainder' ? 'Remainder stays on source' : 'No label')}</span>
                      <span>{row.percentageUsed ? `${row.percentageUsed}%` : '—'}</span>
                    </div>
                    <div className={`mt-3 font-mono text-small font-semibold ${amountTone(row.amount) === 'neg' ? 'text-neg' : amountTone(row.amount) === 'pos' ? 'text-pos' : 'text-ink'}`}>
                      {formatKinr(row.amount)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        <SectionCard section="match" title="Posted rows" padded={false}>
          {ledgerEntries.length === 0 ? (
            <div className="p-4">
              <EmptyState
                section="match"
                icon={ScrollText}
                title="Nothing posted yet"
                description="Once the split engine writes rows, they will appear here."
              />
            </div>
          ) : (
            <>
              <div className="space-y-3 p-4 md:hidden">
                {ledgerEntries.map((row) => (
                  <TimelineRow
                    key={row.id}
                    section="match"
                    title={targetName(accountsById, row.targetMatchAccountId)}
                    subtitle={row.label?.trim() || (row.partKind === 'remainder' ? 'Remainder row' : 'Split row')}
                    date={entry.effectiveDate}
                    amount={formatKinr(row.amount)}
                    amountTone={amountTone(row.amount)}
                    badges={
                      <>
                        <Pill tone={row.partKind === 'split' ? 'amber' : 'neutral'}>{row.partKind}</Pill>
                        {row.percentageUsed && <Pill section="match">{row.percentageUsed}%</Pill>}
                      </>
                    }
                  />
                ))}
              </div>

              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full text-xs">
                  <thead className="border-b border-border text-left text-[10px] uppercase tracking-wide text-ink-muted">
                    <tr>
                      <th className="px-4 py-2.5">Target account</th>
                      <th className="px-4 py-2.5">Kind</th>
                      <th className="px-4 py-2.5">Label</th>
                      <th className="px-4 py-2.5 text-right">% used</th>
                      <th className="px-4 py-2.5 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {ledgerEntries.map((row) => (
                      <DesktopRow key={row.id} row={row} accountsById={accountsById} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </SectionCard>
      </div>
    </MatchShell>
  );
}

function DesktopRow({
  row,
  accountsById,
}: {
  row: MatchLedgerEntry;
  accountsById: Map<number, MatchAccount>;
}) {
  const tone = amountTone(row.amount);
  return (
    <tr>
      <td className="px-4 py-2.5 text-ink">{targetName(accountsById, row.targetMatchAccountId)}</td>
      <td className="px-4 py-2.5 text-ink-muted">{row.partKind}</td>
      <td className="px-4 py-2.5 text-ink-muted">{row.label?.trim() || '—'}</td>
      <td className="px-4 py-2.5 text-right font-mono text-ink-muted">{row.percentageUsed ? `${row.percentageUsed}%` : '—'}</td>
      <td className={['px-4 py-2.5 text-right font-mono', tone === 'neg' ? 'text-neg' : tone === 'pos' ? 'text-pos' : 'text-ink'].join(' ')}>
        {formatKinr(row.amount)}
      </td>
    </tr>
  );
}

function targetName(accountsById: Map<number, MatchAccount>, id: number): string {
  return accountsById.get(id)?.name ?? `#${id}`;
}

function amountTone(raw: string): 'default' | 'pos' | 'neg' {
  const value = Number(raw);
  if (!Number.isFinite(value) || value === 0) return 'default';
  return value < 0 ? 'neg' : 'pos';
}

function formatKinr(raw: string): string {
  const value = Number(raw);
  if (!Number.isFinite(value)) return raw;
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const fixed = abs.toFixed(3);
  const [intPart, fracPart] = fixed.split('.');
  const withCommas = (intPart ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${withCommas}.${fracPart ?? '000'}`;
}
